/**
 * Analytics Hook
 * 
 * React hook for tracking events, identifying users, and managing analytics state.
 */

import { useEffect } from 'react';
import {
  trackEvent,
  trackPageView,
  identifyUser,
  resetUser,
  setUserProperties,
  updatePostHogConsent,
} from '@/lib/analytics';
import { useCookieConsent } from './use-cookie-consent';

// ----------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------

export function useAnalytics() {
  const consent = useCookieConsent();

  // Update PostHog consent when cookie consent changes
  useEffect(() => {
    updatePostHogConsent();
  }, [consent]);

  return {
    /**
     * Track a custom event
     */
    track: (eventName: string, properties?: Record<string, any>) => {
      trackEvent(eventName, properties);
    },

    /**
     * Track a page view
     */
    trackPageView: (path: string) => {
      trackPageView(path);
    },

    /**
     * Identify a user
     */
    identify: (userId: string, properties?: Record<string, any>) => {
      identifyUser(userId, properties);
    },

    /**
     * Reset user identification (call on logout)
     */
    reset: () => {
      resetUser();
    },

    /**
     * Set user properties
     */
    setProperties: (properties: Record<string, any>) => {
      setUserProperties(properties);
    },

    /**
     * Check if analytics is enabled
     */
    isEnabled: consent?.analytics === true,
  };
}
